import { useState } from "react"

import { Form, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { usePhoneCountries } from "../../../../../../hooks/usePhoneCountries"
import { validateQuestion } from "../../utils/sectionValidation"
import { getValueAt, setValueAt } from "../../utils/valuePath"
import CONTROLS from "./controls"
import QuestionLabel from "./QuestionLabel"

import configureStyles from "../../configure.module.less"
import styles from "./questionField.module.less"

const { Text } = Typography

// One question in a Configure step: the labelled Form.Item frame, the validation state,
// and the control its `type` calls for. The frame is all this component owns — every
// type-specific behaviour lives in its own file under controls/.
const QuestionField = ({
  question,
  sectionState,
  buildingBlocks,
  onChange,
  onUpload,
  onRemove,
  showErrors
}) => {
  const { t } = useTranslation()
  // Subscribe to the phone-countries cache so once it loads, PhoneInput's CountrySelect
  // re-renders with the flag list.
  usePhoneCountries()

  const value = getValueAt(sectionState, question.key)
  const [touched, setTouched] = useState(false)

  // A chip-multi with allowOther can be answered with custom "+ Other" chips alone.
  // Mirror sectionValidation / isNextEnabled here so the inline error matches the Next
  // button + progress badge, which already treat a custom chip as filled. Without this a
  // field shows a red "required" error while Next is green.
  const hasCustomChips =
    question.type === "chip-multi" &&
    question.allowOther &&
    (getValueAt(sectionState, `${question.key}_custom`) || []).length > 0
  const errorKey = hasCustomChips ? null : validateQuestion(question, value)
  const showError = (showErrors || touched) && !!errorKey
  const help = showError ? t(errorKey, { min: question?.min ?? 20 }) : null

  const Control = CONTROLS[question.type]
  const controlProps = {
    question,
    value,
    status: showError ? "error" : "",
    // Inputs are fully controlled off `value` (Redux), with no local mirror, so
    // asynchronously-fetched profile data hydrates the form instead of racing a useState
    // initializer. Every keystroke flushes and re-renders, so what is on screen matches
    // the underlying state every tick.
    onChange: (next) => onChange(setValueAt(sectionState, question.key, next)),
    onTouch: () => setTouched(true),
    // Only chip-multi needs these: its "+ Other" values live under a sibling key, so it
    // writes a patch of its own rather than going through `onChange` above.
    sectionState,
    onPatch: onChange,
    buildingBlocks,
    onUpload,
    onRemove
  }

  return (
    <Form.Item
      label={<QuestionLabel question={question} />}
      validateStatus={showError ? "error" : ""}
      className={`${configureStyles.formItem}${question.type === "textarea" ? ` ${configureStyles.formItemTextarea}` : ""}`}
      colon={false}
    >
      {Control ? (
        <Control {...controlProps} />
      ) : (
        <Text type="secondary">{t("Unsupported question type")}</Text>
      )}
      {help ? (
        <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
          {help}
        </Text>
      ) : question.help ? (
        <Text type="secondary" className={styles.help} style={{ marginTop: 4, display: "block" }}>
          {t(question.help)}
        </Text>
      ) : null}
    </Form.Item>
  )
}

export default QuestionField
